import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import useFundAccess from '@/hooks/useFundAccess';
import { worldOfPath } from '@/helpers/permissionWorlds';
import styles from './WorldGate.module.css';

/**
 * A screen of the fund whose permission world is closed to this coordinator (Druk, 22.10.2026)
 * says so, rather than opening empty or failing on its first request. The menus never offer such
 * a screen; this is for an address typed or kept from before the permission changed.
 */
const WorldGate = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { canView, isLoading } = useFundAccess();
  // The router's path is relative to its basename: "/<fundId>/<screen>".
  const fundBase = location.pathname.match(/^\/[^/]+/)?.[0] ?? '';
  const world = worldOfPath(location.pathname.slice(fundBase.length));

  if (!world || isLoading || canView(world)) return <>{children}</>;

  return (
    <div className={styles.panel} role="status">
      <h2 className={styles.title}>{t('permissionWorlds.closed.title')}</h2>
      <p className={styles.text}>{t('permissionWorlds.closed.text', { world: t(`permissionWorlds.names.${world}`) })}</p>
      <Link className={styles.link} to={`${fundBase}/transactions`}>
        {t('permissionWorlds.closed.toBoard')}
      </Link>
    </div>
  );
};

export default WorldGate;
